import { DatePickerWithRange } from '@/entities/datePicker'
import { BaseCard, BaseTabs } from '@/shared/components'
import { StatisticTabNames, statisticTabsButton } from '@/shared/lib/models/tabsStatisticOptions'
import { useIsMobile } from '@/shared/utils'
import { StatisticRefPanel } from '@/entities/referral/ui/referralsStatistics/StatisticRefPanel/StatisticRefPanel'
import { InfoType } from './model'
import { useState, useEffect, useMemo } from 'react'
import { DateRange } from 'react-day-picker'
import { useReferralSummary } from '../../hooks'
import { isToday } from 'date-fns'
import { formatCurrency } from '@/shared/lib/currency'
import { Currencies } from '@/entities/curency'

type ReferralsStatisticsProps = {
  tabName: StatisticTabNames
  setTabName: (value: StatisticTabNames) => void
}

export const ReferralsStatistics = ({ tabName, setTabName }: ReferralsStatisticsProps) => {
  // Инициализируем с текущей датой
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const today = new Date()
    
    return {
      from: today,
      to: today
    }
  })

  const { data: referralSummary } = useReferralSummary({ dateRange })
  
  // Состояние для значений, которые должны обновляться
  const [isCurrentDate, setIsCurrentDate] = useState(true) // Начальное значение true, так как dateRange инициализируется сегодняшней датой
  const [yesterdayBalancePayIn, setYesterdayBalancePayIn] = useState(0)
  const [currentBalance, setCurrentBalance] = useState(0)
  const [currentBalanceFrom, setCurrentBalanceFrom] = useState(0)
  const [payInRefInfo, setPayInRefInfo] = useState<InfoType>({
    orders: 0,
    profit: '0 USDT',
    conversion: '0 %',
    averageTime: 0,
  })

  const handleDateChange = (range: DateRange | undefined) => {
    setDateRange(range)
  }

  const isMobile = useIsMobile()
  
  // Обновляем значения при изменении dateRange или referralSummary
  useEffect(() => {
    setIsCurrentDate(dateRange?.to ? isToday(dateRange.to) : false)
    setYesterdayBalancePayIn(referralSummary?.yesterdayReferralTopupSummary?.totalAmountTo || 0)
    setCurrentBalance(referralSummary?.referralTopupSummary?.totalAmountTo || 0)
    setCurrentBalanceFrom(referralSummary?.referralTopupSummary?.totalAmountFrom || 0)
    
    // Обновляем payInRefInfo
    setPayInRefInfo({
      orders: referralSummary?.referralTopupSummary?.totalCount || 0,
      profit: formatCurrency(referralSummary?.referralProfitInPeriod || 0, Currencies.USDT),
      conversion: (() => {
        const totalCount = referralSummary?.referralTopupSummary?.totalCount || 0
        const paidCount = referralSummary?.referralTopupSummary?.paidCount || 0
        return totalCount > 0 ? `${((paidCount / totalCount) * 100).toFixed(1)} %` : '0 %'
      })(),
      averageTime: referralSummary?.referralTopupSummary?.paidAverageConfirmSeconds || 0,
    })
  }, [dateRange, referralSummary])

  const payOutRefInfo: InfoType = {
    orders: 0,
    profit: formatCurrency(0, Currencies.USDT),
    conversion: '0%',
    averageTime: 0,
  }

  const currentBalancePayOut = 0
  const yesterdayBalanceFromPayOut = 0
  const yesterdayBalancePayOut = 0

  // Мемоизируем tabComponents для предотвращения лишних перерендеров
  const tabComponents = useMemo(() => ({
    payIn: (
      <StatisticRefPanel
        type="payIn"
        data={payInRefInfo}
        currentBalance={currentBalance}
        currentBalanceFrom={currentBalanceFrom}
        yesterdayBalance={yesterdayBalancePayIn}
        showRateInfo={false}
        showBalanceChange={isCurrentDate}
      />
    ),
    payOut: (
      <StatisticRefPanel
        type="payOut"
        data={payOutRefInfo}
        currentBalance={currentBalancePayOut}
        currentBalanceFrom={yesterdayBalanceFromPayOut}
        yesterdayBalance={yesterdayBalancePayOut}
        showRateInfo={false}
        showBalanceChange={isCurrentDate}
      />
    ),
  }), [payInRefInfo, currentBalance, currentBalanceFrom, yesterdayBalancePayIn, isCurrentDate])

  return (
    <BaseCard padding={isMobile ? 'sm' : 'md'} className="flex flex-col gap-6 w-full">
      <div className="flex md:flex-row flex-col justify-between items-start md:items-center gap-2">
        <div className="flex flex-row items-center gap-4 mb-[20px] md:mb-0">
          <div className="font-klein font-medium">
            {isMobile ? 'Статистика' : 'Статистика Реф. Аккаунта'}
          </div>
          <BaseTabs
            tabs={statisticTabsButton.filter(e => e.value === "payIn")}
            selectedTab={tabName}
            onSelect={setTabName}
            direction="row"
            variant="secondary"
          />
        </div>
        <div className="flex md:flex-row flex-col md:items-center gap-4 w-full md:w-fit">
          <DatePickerWithRange
            onDateChange={handleDateChange}
          />
        </div>
      </div>
      {tabComponents[tabName]}
    </BaseCard>
  )
}
